import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Download, 
  Sparkles, 
  AlertTriangle,
  TrendingUp,
  Target,
  Users,
  Globe,
  Lock,
  CheckCircle,
  XCircle,
  ArrowRight,
  Euro,
  Star,
  Trophy
} from 'lucide-react';

export const PreAuditProLePresident: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'from-green-50 to-green-100';
    if (score >= 40) return 'from-orange-50 to-orange-100';
    return 'from-red-50 to-red-100';
  };

  const getPriorityBadge = (priority: string) => {
    if (priority === 'Critique') return 'bg-red-500';
    if (priority === 'Urgent') return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Actions Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-stratos-gold" />
            <h1 className="text-xl font-bold text-gray-900">Pre-Audit Pro - Le President Lyon</h1>
          </div>
          <div className="flex gap-3">
            <Button onClick={handlePrint} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Imprimer / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={contentRef} className="max-w-[240mm] mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full">
        
        {/* PAGE 1: COUVERTURE */}
        <div className="min-h-screen relative bg-gradient-to-br from-gray-900 via-gray-800 to-black p-20 flex flex-col justify-between page-break">
          <div>
            <div className="flex items-center gap-4 mb-12">
              <Sparkles className="w-16 h-16 text-yellow-400" />
              <div>
                <h1 className="text-6xl font-bold text-yellow-400 drop-shadow-2xl">STRATOS</h1>
                <p className="text-white text-base tracking-widest mt-2 font-semibold">HOSPITALITY DIGITAL STRATEGY</p>
              </div>
            </div>
            
            <div className="mt-20">
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-6 py-2 text-sm mb-8">
                <Lock className="w-4 h-4 mr-2" />
                DOCUMENT CONFIDENTIEL
              </Badge>
              
              <h2 className="text-7xl font-bold text-white mb-8 leading-tight drop-shadow-2xl">
                Audit Strategique
                <br />
                & Digital
              </h2>
              
              <h3 className="text-6xl font-bold text-yellow-400 mb-16 drop-shadow-2xl">
                Le President
              </h3>
              
              <div className="grid grid-cols-2 gap-8 text-white/80 text-lg max-w-2xl">
                <div>
                  <p className="text-white/50 text-sm uppercase tracking-wide mb-2">Restaurant</p>
                  <p className="font-semibold">Gastronomie Francaise</p>
                  <p className="text-sm">Lyon 6eme - Parc Tete d'Or</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm uppercase tracking-wide mb-2">Chef</p>
                  <p className="font-semibold">Christophe Marguin</p>
                  <p className="text-sm">President Toques Blanches Lyonnaises</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm uppercase tracking-wide mb-2">Distinctions</p>
                  <p className="font-semibold">Gault&Millau 14/20</p>
                  <p className="text-sm">Assiette Michelin - Travellers' Choice</p>
                </div>
                <div>
                  <p className="text-white/50 text-sm uppercase tracking-wide mb-2">Contact</p>
                  <p className="font-semibold">Christophe Marguin</p>
                  <p className="text-sm">+33 4 78 94 51 17</p>
                </div>
              </div>
            </div>
          </div>

          {/* Score Principal */}
          <div className="bg-white/95 backdrop-blur-sm border-4 border-stratos-gold rounded-3xl p-12 shadow-2xl mt-12">
            <div className="grid grid-cols-2 gap-16">
              <div className="text-center">
                <p className="text-gray-600 text-base mb-4 uppercase tracking-wide font-semibold">Score Digital Actuel</p>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <span className="text-9xl font-bold text-orange-500">59</span>
                  <span className="text-5xl text-gray-400">/100</span>
                </div>
                <p className="text-gray-600 text-sm">WooRank - Zone d'amelioration</p>
              </div>
              
              <div className="border-l-4 border-stratos-gold pl-12 text-center">
                <p className="text-gray-600 text-base mb-4 uppercase tracking-wide font-semibold">Potentiel Identifie</p>
                <div className="flex items-center justify-center gap-4 mb-4">
                  <span className="text-8xl font-bold text-green-500">85</span>
                  <span className="text-4xl text-gray-400">/100</span>
                </div>
                <p className="text-green-600 font-bold text-xl">+26 points</p>
                <p className="text-gray-600 text-sm mt-2">Delai: 3-6 mois</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between text-white/40 text-sm border-t border-white/10 pt-6 mt-8">
            <p>12 Janvier 2026</p>
            <p>Audit Confidentiel</p>
            <p>Page 1/12</p>
          </div>
        </div>

        {/* PAGE 2: TABLEAU DIAGNOSTIC */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Tableau de Synthese - Diagnostic</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
            <p className="text-gray-600 mt-4">Credibilisation et diagnostic d'opportunite</p>
          </div>

          {/* Table Diagnostics */}
          <div className="space-y-4 mb-12">
            {[
              {
                domaine: 'Reservation Client (UX Critique)',
                obs: 'PROBLEME MAJEUR: CTA "Reserver" mene uniquement a la privatisation. Client lambda ne peut PAS reserver en ligne pour un repas classique.',
                score: 10,
                color: 'red',
                bgColor: 'bg-red-50',
                borderColor: 'border-red-300'
              },
              {
                domaine: 'Visibilite SEO Locale',
                obs: 'Position moyenne 24.5 (page 3). Invisible sur "restaurant chic Lyon 6" et "restaurant Tete d\'Or". Trafic 3,150/mois correctmais potentiel 8,000+.',
                score: 35,
                color: 'orange',
                bgColor: 'bg-orange-50',
                borderColor: 'border-orange-300'
              },
              {
                domaine: 'E-Reputation Multi-Plateformes',
                obs: 'Excellence: 9.4 TheFork (300 avis), 4.4 Google (282 avis), 4.3 TripAdvisor Travellers\' Choice. MAIS volume insuffisant vs concurrents.',
                score: 75,
                color: 'green',
                bgColor: 'bg-green-50',
                borderColor: 'border-green-300'
              },
              {
                domaine: 'Visibilite IA 2026',
                obs: 'AI Visibility 26/100. Seulement 4 pages citees par ChatGPT/Gemini. Llms.txt absent. Schema.org manquant. 40% recherches 2026 via IA.',
                score: 20,
                color: 'red',
                bgColor: 'bg-red-50',
                borderColor: 'border-red-300'
              },
              {
                domaine: 'Identite & Storytelling',
                obs: 'Collection 4000 menus presidentiels (Napoleon III a Macron) = actif UNIQUE AU MONDE. Heritage Edouard Herriot. Sous-exploite digitalement.',
                score: 45,
                color: 'orange',
                bgColor: 'bg-orange-50',
                borderColor: 'border-orange-300'
              },
              {
                domaine: 'Presence Locale GMB',
                obs: 'Fiche existe mais mal detectee (WooRank: "Missing GMB"). Completion ~65%. Position 4 Local Pack = hors podium TOP 3.',
                score: 40,
                color: 'orange',
                bgColor: 'bg-orange-50',
                borderColor: 'border-orange-300'
              }
            ].map((item, idx) => (
              <Card key={idx} className={`p-6 ${item.bgColor} ${item.borderColor} border-2`}>
                <div className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-3">{item.domaine}</h3>
                    <p className="text-gray-800 text-base leading-relaxed">{item.obs}</p>
                  </div>
                  <div className="flex flex-col items-center bg-white rounded-lg px-6 py-4 shadow-sm">
                    <div className={`text-6xl font-bold ${item.color === 'red' ? 'text-red-600' : item.color === 'orange' ? 'text-orange-600' : 'text-green-600'}`}>
                      {item.score}
                    </div>
                    <div className="text-gray-500 text-sm font-semibold">/100</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Constats Cles */}
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 p-8">
            <h3 className="text-2xl font-bold text-red-900 mb-6 flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              Constats Cles
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                <p className="text-red-900 font-semibold">
                  Priorite: Parcours reservation a optimiser - Le CTA "Reserver" pourrait etre ameliore pour distinguer reservation classique et privatisation
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <p className="text-gray-800">
                  Opportunite: Collection 4000 menus presidentiels (Napoleon III → Macron) = actif unique a valoriser davantage sur le web
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                <p className="text-gray-800">
                  Opportunite marketing: Lien potentiel avec "Le President" de Bernachon (gateau mythique lyonnais) a explorer
                </p>
              </div>
            </div>
          </Card>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 2/12
          </div>
        </div>

        {/* PAGE 3: IMPACT FINANCIER */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Impact Financier Estime</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
          </div>

          {/* Grille KPIs */}
          <div className="grid grid-cols-3 gap-6 mb-12">
            <Card className="p-8 bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300">
              <div className="flex items-start justify-between mb-4">
                <AlertTriangle className="w-10 h-10 text-red-600" />
                <Badge className="bg-red-600 text-white">Potentiel Non Capte</Badge>
              </div>
              <p className="text-sm text-gray-600 mb-3">Estimation opportunites</p>
              <p className="text-5xl font-bold text-red-600">60-100k</p>
              <p className="text-sm text-gray-500 mt-2">EUR/an (estimation prudente)</p>
              <div className="mt-4 space-y-2 text-xs text-gray-700">
                <p>• SEO ameliorable: 25-40k EUR</p>
                <p>• UX reservation: 20-30k EUR</p>
                <p>• International: 15-30k EUR</p>
              </div>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400">
              <div className="flex items-start justify-between mb-4">
                <TrendingUp className="w-10 h-10 text-yellow-600" />
                <Badge className="bg-yellow-600 text-white">Potentiel An 1</Badge>
              </div>
              <p className="text-sm text-gray-600 mb-3">Objectif realiste 12 mois</p>
              <p className="text-5xl font-bold text-yellow-600">50-80k</p>
              <p className="text-sm text-gray-500 mt-2">EUR CA additionnel (conservateur)</p>
              <div className="mt-4 space-y-2 text-xs text-gray-700">
                <p>• Quick wins (3 mois): 15-20k EUR</p>
                <p>• SEO + GMB (6 mois): 25-40k EUR</p>
                <p>• International (12 mois): 10-20k EUR</p>
              </div>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-400">
              <div className="flex items-start justify-between mb-4">
                <Target className="w-10 h-10 text-green-600" />
                <Badge className="bg-green-600 text-white">ROI Pack Premium</Badge>
              </div>
              <p className="text-sm text-gray-600 mb-3">Retour estime</p>
              <p className="text-5xl font-bold text-green-600">200-300%</p>
              <p className="text-sm text-gray-500 mt-2">Sur 12 mois (estimation prudente)</p>
              <div className="mt-4 space-y-2 text-xs text-gray-700">
                <p>• Investissement: 24k EUR/an</p>
                <p>• Objectif retour: 50-75k EUR</p>
                <p>• Variables selon execution</p>
              </div>
            </Card>
          </div>

          {/* Points Cles */}
          <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-10 mb-8">
            <h3 className="text-3xl font-bold mb-6 text-yellow-400 drop-shadow-lg">Resume Executif</h3>
            <div className="grid grid-cols-2 gap-6 text-base">
              <div>
                <p className="text-white/80 mb-3 uppercase tracking-wide text-sm font-semibold">Situation</p>
                <p className="leading-relaxed text-white">
                  Institution lyonnaise d'excellence (Gault&Millau 14/20, Assiette Michelin) avec reputation 
                  solide (9.4 TheFork) MAIS visibilite digitale a optimiser et parcours client a ameliorer.
                </p>
              </div>
              <div>
                <p className="text-white/80 mb-3 uppercase tracking-wide text-sm font-semibold">Points d'Attention</p>
                <p className="leading-relaxed text-white">
                  Reservation en ligne a corriger + Position Google ameliorable (page 3) 
                  + Collection 4000 menus a valoriser digitalement.
                </p>
              </div>
            </div>
          </Card>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 3/12 - Diagnostic financier et resume executif
          </div>
        </div>

        {/* PAGE 4: PLAN D'ACTION STRATEGIQUE */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Plan d'Action Strategique</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
            <p className="text-gray-600 mt-4">Actions prioritaires et impact estime</p>
          </div>

          {/* Tableau Actions */}
          <div className="space-y-6">
            {[
              {
                titre: 'URGENCE P0: Reparer le Parcours Reservation',
                desc: 'Le CTA "Reserver" doit mener a TheFork/Widget reservation, PAS uniquement privatisation',
                score: 5,
                priorite: 'Critique',
                impact: 'Amelioration conversion estimee 20-30 resa/mois',
                delai: '48h - Modification technique simple',
                actions: ['Ajouter widget TheFork sur home', 'CTA "Reserver une table" distinct de "Privatiser"', 'Test UX immediat']
              },
              {
                titre: 'Optimisation SEO: Ameliorer Positionnement',
                desc: 'Position 24.5 ameliorable. Objectif: 10-15 mots-cles en page 1 sur "restaurant chic Lyon 6"',
                score: 15,
                priorite: 'Critique',
                impact: 'Augmentation trafic estimee +80-120% sur 6-12 mois',
                delai: '3-6 mois',
                actions: ['Landing page "Restaurant Tete d\'Or Lyon"', 'Optimiser "restaurant chic Lyon 6"', 'Content cluster Lyon 6eme (10 articles)', '+20 autres actions']
              },
              {
                titre: 'Valorisation Heritage: Collection Menus Presidentiels',
                desc: 'Unique au monde (Napoleon III → Macron). Levier storytelling touristique a developper',
                score: 10,
                priorite: 'Urgent',
                impact: 'Differenciation unique + potentiel tourisme premium',
                delai: '1-2 mois',
                actions: ['Page dediee "Musee Menus Presidentiels"', 'Partenariat Bernachon (gateau President)', 'Experience "Tea Time Presidentiel"', '+8 autres actions']
              },
              {
                titre: 'Optimisation Local Pack & GMB',
                desc: 'Position ameliorable dans Local Pack. GMB 65% complet, opportunite optimisation.',
                score: 40,
                priorite: 'Urgent',
                impact: 'Amelioration visibilite locale estimee +30-50 resa/mois',
                delai: '2-4 mois',
                actions: ['Completion GMB 100%', 'Strategie avis (objectif 450 avis)', 'Posts GMB 3x/semaine', '+12 autres actions']
              }
            ].map((action, idx) => (
              <Card key={idx} className="overflow-hidden">
                <div className="flex items-start">
                  <div className={`w-2 h-full ${action.priorite === 'Critique' ? 'bg-red-500' : 'bg-orange-500'}`}></div>
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{action.titre}</h3>
                        <p className="text-gray-700">{action.desc}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className={`text-6xl font-bold ${getScoreColor(action.score)}`}>
                          {action.score}
                        </div>
                        <Badge className={getPriorityBadge(action.priorite)}>{action.priorite}</Badge>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Impact Estime</p>
                        <p className="font-semibold text-gray-900">{action.impact}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Delai</p>
                        <p className="font-semibold text-gray-900">{action.delai}</p>
                      </div>
                    </div>

                    {idx < 2 && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500 mb-2">Actions a realiser:</p>
                        <ul className="space-y-1">
                          {action.actions.map((a, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {a}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {idx >= 2 && (
                      <div className="mt-4 bg-gray-900 rounded-lg p-4 text-center">
                        <Lock className="w-6 h-6 text-stratos-gold mx-auto mb-2" />
                        <p className="text-white/80 text-sm">Details complets dans Pack Premium</p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 4/12 - 4 axes strategiques identifies - Actions P0 et P1 detaillees
          </div>
        </div>

        {/* PAGE 5: ANALYSE CONCURRENTIELLE */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Analyse Concurrentielle Lyon 6eme</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
            <p className="text-gray-600 mt-4">Benchmark avec les leaders gastronomiques du quartier</p>
          </div>

          {/* Tableau Comparatif */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left text-sm font-bold">Indicateur</th>
                  <th className="p-4 text-center text-sm font-bold bg-stratos-gold text-gray-900">Le President</th>
                  <th className="p-4 text-center text-sm font-bold">Milpree</th>
                  <th className="p-4 text-center text-sm font-bold">Atelier Saveurs</th>
                  <th className="p-4 text-center text-sm font-bold">Le Splendid</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { label: 'Note TheFork', vous: '9.4/10', m: '8.8/10', a: '9.2/10', s: '8.5/10' },
                  { label: 'Note Google', vous: '4.4★ (282)', m: '4.9★ (122)', a: '4.6★ (776)', s: '4.3★ (2086)' },
                  { label: 'Ticket Moyen', vous: '75-100 EUR', m: '35-45 EUR', a: '35-50 EUR', s: '50-70 EUR' },
                  { label: 'Trafic Web/mois', vous: '3,150', m: '~1,800', a: '~4,200', s: '~12,000' },
                  { label: 'Points Forts', vous: 'Excellence, Heritage', m: 'Fresh, Value', a: 'Welcoming, Tasty', s: 'Location, Volume' },
                  { label: 'Faiblesse', vous: 'Prix percu eleve', m: 'Petites portions', a: 'Service speed', s: 'Tourist trap' }
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 text-sm">{row.label}</td>
                    <td className="p-4 text-center font-bold text-stratos-gold bg-yellow-50">{row.vous}</td>
                    <td className="p-4 text-center text-gray-700">{row.m}</td>
                    <td className="p-4 text-center text-gray-700">{row.a}</td>
                    <td className="p-4 text-center text-gray-700">{row.s}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Insights */}
          <div className="grid grid-cols-3 gap-6">
            <Card className="p-8 bg-white border-2 border-green-400 shadow-lg">
              <Trophy className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="font-bold text-gray-900 text-xl mb-4">Vos Forces</h3>
              <ul className="space-y-3 text-base text-gray-800">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Excellence reconnue (9.4 TheFork)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Heritage unique (4000 menus)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Chef mediatique (Toques Blanches)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Positionnement premium clair
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 border-orange-400 shadow-lg">
              <AlertTriangle className="w-12 h-12 text-orange-600 mb-4" />
              <h3 className="font-bold text-gray-900 text-xl mb-4">Points d'Attention</h3>
              <ul className="space-y-3 text-base text-gray-800">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Trafic web inferieur a Atelier/Splendid
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Volume avis inferieur concurrents
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Prix percu eleve (education client)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  Digitalisation en retard
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white border-2 border-blue-400 shadow-lg">
              <Target className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="font-bold text-gray-900 text-xl mb-4">Opportunites</h3>
              <ul className="space-y-3 text-base text-gray-800">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Storytelling presidentiel
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Partenariat Bernachon
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  SEO "restaurant chic Lyon 6"
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Tourisme haut de gamme US/JP
                </li>
              </ul>
            </Card>
          </div>

          <div className="mt-12 text-center text-gray-400 text-sm">
            Page 5/12 - Benchmark concurrentiel - Excellence produit confirmee - Digital a transformer
          </div>
        </div>

        {/* PAGE 6: MOTS-CLES & SEO */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Mots-Cles & Opportunites SEO</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
          </div>

          {/* Tableau Keywords */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Positionnement Actuel (10 Mots-Cles Prioritaires)</h3>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-3 text-left">Mot-cle</th>
                  <th className="p-3 text-right">Volume</th>
                  <th className="p-3 text-right">Position</th>
                  <th className="p-3 text-center">Statut</th>
                  <th className="p-3 text-right">Opportunite</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { kw: 'le president lyon', vol: 680, pos: 2, st: 'OK', opp: '92%' },
                  { kw: 'restaurant le president', vol: 320, pos: 3, st: 'OK', opp: '88%' },
                  { kw: 'president lyon 6', vol: 240, pos: 5, st: 'Bon', opp: '85%' },
                  { kw: 'restaurant chic lyon 6', vol: 1450, pos: 35, st: 'Critique', opp: '45%' },
                  { kw: 'restaurant tete d\'or lyon', vol: 980, pos: 28, st: 'Critique', opp: '52%' },
                  { kw: 'restaurant lyon 6', vol: 3800, pos: 32, st: 'Critique', opp: '45%' },
                  { kw: 'restaurant gastronomique lyon', vol: 2400, pos: 35, st: 'Critique', opp: '42%' },
                  { kw: 'dejeuner affaires lyon', vol: 720, pos: 40, st: 'Critique', opp: '38%' },
                  { kw: 'restaurant traditionnel lyon', vol: 920, pos: 40, st: 'Critique', opp: '38%' },
                  { kw: 'restaurant groupe lyon 6', vol: 380, pos: 45, st: 'Critique', opp: '35%' }
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-3 font-medium text-gray-900">{row.kw}</td>
                    <td className="p-3 text-right text-gray-700">{row.vol}</td>
                    <td className="p-3 text-right font-bold text-gray-900">{row.pos}</td>
                    <td className="p-3 text-center">
                      <Badge className={row.st === 'OK' ? 'bg-green-500' : row.st === 'Bon' ? 'bg-blue-500' : 'bg-red-500'}>
                        {row.st}
                      </Badge>
                    </td>
                    <td className="p-3 text-right text-gray-700">{row.opp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Analyse */}
          <Card className="bg-white border-l-4 border-red-500 shadow-lg p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              ANALYSE CRITIQUE
            </h3>
            <ul className="space-y-3 text-base text-gray-800">
              <li className="flex items-start gap-3">
                <span className="text-green-600 font-bold">✓</span>
                3 mots-cles branded bien positionnes (positions 2-5) = clientele qui vous connait deja
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold">✗</span>
                MAIS 7 mots-cles commerciaux haute valeur en positions 28-45 = INVISIBLES
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold">✗</span>
                "restaurant chic Lyon 6" (1450 rech/mois) position 35 = vous manquez 50-70 clients/mois
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-600 font-bold">✗</span>
                "restaurant Tete d'Or" (980 rech/mois) position 28 = localisation premium non capitalisee
              </li>
              <li className="flex items-start gap-3">
                <span className="text-orange-600 font-bold">!</span>
                Volume total: ~13,000 rech/mois identifies, trafic capte: 3,150 (24% seulement)
              </li>
            </ul>
          </Card>

          <Card className="bg-white border-l-4 border-green-500 shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              QUICK WINS MOTS-CLES (30 Jours)
            </h3>
            <div className="grid grid-cols-2 gap-6 text-base">
              <ul className="space-y-3 text-gray-800">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Landing page "Restaurant Chic Lyon 6"
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Page "Restaurant Parc Tete d'Or"
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Optimiser home "gastronomique Lyon"
                </li>
              </ul>
              <ul className="space-y-3 text-gray-800">
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Page "Dejeuner Affaires Lyon 6"
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Content cluster "Lyon 6eme"
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Schema.org + Llms.txt immediat
                </li>
              </ul>
            </div>
            <div className="mt-6 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg p-5 text-center font-bold text-lg shadow-lg">
              Impact: 15-25 mots-cles page 1 en 3-6 mois = +150-200% trafic
            </div>
          </Card>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 6/12 - Analyse 10 mots-cles strategiques - 76% opportunites manquees
          </div>
        </div>

        {/* PAGE 7: L'ACTIF UNIQUE - COLLECTION PRESIDENTIELLE */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">L'Actif Unique: Collection Presidentielle</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
            <p className="text-gray-600 mt-4">4000 menus presidentiels - Napoleon III a Macron - Unique au monde</p>
          </div>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 p-8 mb-8">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Trophy className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-purple-900 mb-3">Heritage Presidentiel Unique</h3>
                <p className="text-gray-800 leading-relaxed mb-4">
                  Collection de <span className="font-bold text-purple-900">4000 menus presidentiels et royaux</span> accumulee 
                  par la dynastie Marguin. De Napoleon III a Emmanuel Macron, ce patrimoine gastronomique est UNIQUE AU MONDE.
                </p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-white/50 rounded-lg p-3">
                    <p className="text-purple-900 font-semibold">Lien Edouard Herriot</p>
                    <p className="text-gray-700">Ancien maire Lyon & President du Conseil - Client fidele</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-3">
                    <p className="text-purple-900 font-semibold">Depuis 1906</p>
                    <p className="text-gray-700">Dynastie Marguin - 118 ans expertise</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Problème Actuel */}
          <Card className="bg-red-50 border-2 border-red-300 p-6 mb-8">
            <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-3">
              <XCircle className="w-6 h-6" />
              PROBLEME: Actif Totalement Invisible en Ligne
            </h3>
            <ul className="space-y-2 text-sm text-gray-800">
              <li>• Aucune page dediee "Collection Menus Presidentiels" sur le site</li>
              <li>• Aucune mention dans meta description ou title tags</li>
              <li>• 0 article de blog racontant l'histoire de cette collection</li>
              <li>• Non mentionne dans fiches Google/TripAdvisor/TheFork</li>
              <li>• Touristes US/JP (qui adorent ce type de patrimoine) ignorent totalement son existence</li>
            </ul>
          </Card>

          {/* Solution Bernachon */}
          <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">OPPORTUNITE OR: Le Cross-Over Bernachon</h3>
            <p className="text-gray-800 leading-relaxed mb-6">
              Le gateau "Le President" cree par Bernachon en 1975 pour Paul Bocuse partage votre nom. 
              C'est une <span className="font-bold text-orange-600">collision semantique en OR</span> pour le marketing.
            </p>
            
            <div className="bg-white rounded-lg p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">L'Experience "Tea Time Presidentiel" (Concept)</h4>
              <p className="text-gray-700 text-sm mb-4">
                "Degustez le mythique gateau President de Bernachon a la table du President, 
                entoure des menus des Presidents de la Republique."
              </p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-semibold text-gray-900">Prix suggere:</p>
                  <p className="text-gray-700">45 EUR (gateau + cafe + visite collection)</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Cible:</p>
                  <p className="text-gray-700">Touristes US/JP/UK - Groupes culturels</p>
                </div>
              </div>
            </div>

            <div className="bg-orange-600 text-white rounded-lg p-4 text-center font-bold">
              Impact Estime: 20-30 "Tea Time"/mois = 27-40k EUR/an CA additionnel
            </div>
          </Card>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 7/12 - Collection unique inexploitee - Opportunite Bernachon identifiee
          </div>
        </div>

        {/* PAGE 8: PROBLEME UX CRITIQUE */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Probleme UX Critique: Reservation Cassee</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-red-600 to-red-400"></div>
          </div>

          <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white p-10 mb-8">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
              <div>
                <h3 className="text-3xl font-bold mb-4">URGENCE ABSOLUE P0</h3>
                <p className="text-xl leading-relaxed">
                  Le bouton "Reserver" sur votre site mene UNIQUEMENT a la page privatisation. 
                  Un client lambda ne peut PAS reserver une table pour un repas classique en ligne.
                </p>
              </div>
            </div>
          </Card>

          {/* Impact */}
          <div className="grid grid-cols-2 gap-6 mb-8">
            <Card className="p-6 bg-red-50 border-2 border-red-300">
              <h3 className="font-bold text-red-900 mb-4">Impact Business</h3>
              <ul className="space-y-3 text-sm text-gray-800">
                <li className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>40-60 reservations/mois perdues (clients abandonnent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Perte estimee: 40-60k EUR/an de CA</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Experience client degradee (frustration)</span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span>Signal negatif pour Google (taux rebond eleve)</span>
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-green-50 border-2 border-green-300">
              <h3 className="font-bold text-green-900 mb-4">Solution Immediate (48h)</h3>
              <ul className="space-y-3 text-sm text-gray-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Ajouter widget TheFork sur homepage</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>CTA distinct: "Reserver une Table" vs "Privatiser"</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Test UX complet du parcours</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Suivi conversion avec Google Analytics</span>
                </li>
              </ul>
            </Card>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <p className="text-sm text-gray-800">
              <span className="font-bold text-yellow-900">NOTE:</span> Cette erreur UX est probablement la cause #1 
              de perte de CA. C'est un quick win majeur a corriger AVANT tout le reste. ROI immediat sous 7 jours.
            </p>
          </div>

          <div className="mt-12 text-center text-gray-400 text-sm">
            Page 8/12 - Probleme UX critique identifie - Correction urgente requise - Impact 40-60k EUR/an
          </div>
        </div>

        {/* PAGE 9: PERSONAS & CIBLES */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Cartographie des Personas Cibles</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
            <p className="text-gray-600 mt-4">3 typologies clients aux besoins digitaux radicalement differents</p>
          </div>

          <div className="space-y-6">
            {[
              {
                name: 'L\'Heritier (Local / Business)',
                desc: '50-70 ans, Lyonnais, chef entreprise. Cherche reconnaissance, table habituelle, discretion.',
                current: 'Coeur de cible actuel. Parfaitement servi par offre "Salons Prives". Fidelisation forte.',
                digital: 'Reserve par telephone. Utilise peu reseaux sociaux.',
                action: 'Continuer excellence service. Ajouter programme fidelite digital (CRM).',
                color: 'from-blue-50 to-blue-100',
                icon: Users
              },
              {
                name: 'L\'Explorateur Gastronomique (Touriste)',
                desc: '30-60 ans, Americain/Japonais/Europeen. Planifie via Michelin, TripAdvisor. Veut experience "Bocuse".',
                current: 'Visibilite bonne sur guides MAIS site manque contenu narratif EN pour declencher reve.',
                digital: 'Cherche storytelling, histoire, produit iconique (Bresse). Sensible aux labels.',
                action: 'Site EN enrichi. Page "Heritage & Collection Menus". Experience "Tea Time Presidentiel".',
                color: 'from-green-50 to-green-100',
                icon: Globe
              },
              {
                name: 'Le Millennial "Foodie" (Local Actif)',
                desc: '25-45 ans, travaille Cite Internationale. Cherche dejeuner affaires "cool" ou diner Instagram.',
                current: 'POINT FAIBLE. Percoit Le President comme "vieux jeu" ou trop cher. Client naturel de Milpree.',
                digital: 'Sensible sourcing, vin nature, decor. Cherche contenu video, dynamisme.',
                action: 'Communication Instagram/TikTok. Videos "geste cuisine". Menu midi accessible 35-45 EUR.',
                color: 'from-orange-50 to-orange-100',
                icon: Sparkles
              }
            ].map((persona, idx) => {
              const Icon = persona.icon;
              return (
                <Card key={idx} className={`overflow-hidden bg-gradient-to-r ${persona.color} border-2 ${idx === 2 ? 'border-orange-300' : idx === 1 ? 'border-green-300' : 'border-blue-300'}`}>
                  <div className="p-8">
                    <div className="flex items-start gap-6 mb-6">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${idx === 2 ? 'bg-orange-600' : idx === 1 ? 'bg-green-600' : 'bg-blue-600'}`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{persona.name}</h3>
                        <p className="text-gray-700">{persona.desc}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Etat Actuel</p>
                        <p className="text-gray-700">{persona.current}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Comportement Digital</p>
                        <p className="text-gray-700">{persona.digital}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 bg-white/70 rounded-lg p-4">
                      <p className="font-semibold text-gray-900 mb-1 text-sm">Actions Recommandees:</p>
                      <p className="text-gray-800 text-sm">{persona.action}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 9/12 - 3 personas identifies - Strategie differentielle par cible
          </div>
        </div>

        {/* PAGE 10: CHATBOT IA & INTERNATIONAL */}
        <div className="min-h-screen p-16 page-break">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">Leviers Complementaires</h2>
            <div className="w-32 h-1 bg-gradient-to-r from-stratos-gold to-orange-500"></div>
          </div>

          {/* Chatbot */}
          <Card className="p-8 mb-8 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Chatbot IA Conversationnel 24/7</h3>
            <p className="text-gray-700 mb-6">
              67% visiteurs quittent sans reserver faute de reponse immediate. Un chatbot IA GPT-4 
              multilingue (FR/EN/ES/PT) capture ces opportunites.
            </p>
            
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Dispo', val: '24/7', col: 'bg-blue-600' },
                { label: 'Langues', val: '4', col: 'bg-purple-600' },
                { label: 'Temps', val: '<3s', col: 'bg-green-600' },
                { label: 'Conv.', val: '+35%', col: 'bg-orange-600' }
              ].map(s => (
                <div key={s.label} className={`${s.col} rounded-lg p-4 text-white text-center`}>
                  <p className="text-xs mb-1">{s.label}</p>
                  <p className="text-2xl font-bold">{s.val}</p>
                </div>
              ))}
            </div>

            <div className="bg-green-600 text-white rounded-lg p-4 text-center font-bold">
              Impact: 15-25 resa/mois = 12-20k EUR/an (Option +200 EUR/mois Premium)
            </div>
          </Card>

          {/* International */}
          <Card className="p-8 bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-300">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Strategie Internationale & Tourisme</h3>
            <p className="text-gray-700 mb-6">
              Lyon: 800k+ touristes etrangers/an. 60% anglophones. Vous ne captez que 120 visites/mois internationales.
            </p>
            
            <table className="w-full text-sm mb-6">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-2 text-left">Langue</th>
                  <th className="p-2 text-right">Coverage</th>
                  <th className="p-2 text-right">Trafic Actuel</th>
                  <th className="p-2 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['FR', '100%', '2,900/mois', 'OK', 'bg-green-500'],
                  ['EN', '45%', '120/mois', 'Faible', 'bg-orange-500'],
                  ['ES', '20%', '80/mois', 'Critique', 'bg-red-500'],
                  ['PT', '10%', '50/mois', 'Critique', 'bg-red-500']
                ].map((lang, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-2 font-semibold">{lang[0]}</td>
                    <td className="p-2 text-right">{lang[1]}</td>
                    <td className="p-2 text-right">{lang[2]}</td>
                    <td className="p-2 text-center">
                      <Badge className={lang[4] as string}>{lang[3]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-orange-600 text-white rounded-lg p-4 text-center font-bold">
              Potentiel: +600% trafic international (120 → 800/mois) = 30-50k EUR/an
            </div>
          </Card>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 10/12 - Chatbot IA +35% conversion - International sous-exploite
          </div>
        </div>

        {/* PAGE 11: PACK PREMIUM */}
        <div className="min-h-screen p-16 page-break">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-4">Transformez Votre</h2>
            <h2 className="text-5xl font-bold text-stratos-gold mb-8">Visibilite Digitale</h2>
            <p className="text-xl text-gray-600">120-200k EUR de croissance identifies</p>
          </div>

          {/* Pack Premium Pleine Page */}
          <Card className="bg-gradient-to-br from-yellow-50 via-orange-50 to-yellow-50 border-4 border-stratos-gold p-12 mb-8">
            <div className="text-center mb-8">
              <Badge className="bg-stratos-gold text-gray-900 text-lg px-6 py-2 mb-6">
                <Star className="w-5 h-5 mr-2" />
                RECOMMANDE PAR STRATOS
              </Badge>
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Pack Premium</h3>
              <p className="text-lg text-gray-700 mb-8">Solution complete SEO + GEO + Content + Consulting</p>
              
              <div className="flex items-baseline justify-center gap-4 mb-4">
                <span className="text-7xl font-bold text-stratos-gold">2,000</span>
                <span className="text-3xl text-gray-600">EUR/mois</span>
              </div>
              <p className="text-gray-500">Engagement 12 mois - Facturation mensuelle</p>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              {[
                'Audit technique complet + corrections P0',
                'Optimisation SEO complete (100+ KW)',
                'Gestion GMB professionnelle',
                'Netlinking qualite (50 backlinks/an)',
                'Content marketing (24 articles/an)',
                'Strategie internationale (3 langues)',
                'Consulting mensuel (2h/mois)',
                'Reporting detaille + Dashboard'
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{f}</span>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-lg p-6 text-center text-white shadow-xl">
              <p className="text-base mb-3 uppercase tracking-wide font-semibold">ROI Objectif</p>
              <p className="text-5xl font-bold mb-2">200-300%</p>
              <p className="text-sm opacity-90">Variables multiples - Estimation prudente</p>
            </div>
          </Card>

          {/* Prochaines Etapes */}
          <div className="grid grid-cols-3 gap-6 mb-8">
            {[
              { num: '1', title: 'Rendez-vous', desc: 'Echange 30-45 min avec nos experts' },
              { num: '2', title: 'Audit Complet', desc: 'Analyse technique approfondie sur site' },
              { num: '3', title: 'Demarrage J+1', desc: 'Correction UX reservation + Quick wins' }
            ].map(step => (
              <Card key={step.num} className="p-6 text-center">
                <div className="w-16 h-16 bg-stratos-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-gray-900">{step.num}</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.desc}</p>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center text-gray-400 text-sm">
            Page 11/12 - Pack Premium recommande - 2,000 EUR/mois - ROI 300-400%
          </div>
        </div>

        {/* PAGE 12: CONTACT FINAL */}
        <div className="min-h-screen p-16 page-break bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-4 mb-12">
              <Sparkles className="w-12 h-12 text-yellow-400" />
              <div>
                <h1 className="text-5xl font-bold text-yellow-400 drop-shadow-2xl">STRATOS</h1>
                <p className="text-white text-sm tracking-widest mt-1 font-semibold">HOSPITALITY DIGITAL STRATEGY</p>
              </div>
            </div>

            <div className="max-w-3xl">
              <h2 className="text-4xl font-bold text-white mb-8">Pret a Propulser<br />Votre Visibilite ?</h2>
              
              <p className="text-xl text-white/80 leading-relaxed mb-12">
                Le President possede tous les atouts pour renforcer sa position gastronomique Lyon 6eme. 
                L'audit identifie un potentiel d'optimisation de <span className="text-yellow-400 font-bold">50-80k EUR/an</span> (estimation prudente).
              </p>

              <div className="grid grid-cols-2 gap-8 mb-12">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
                  <h3 className="text-stratos-gold font-bold mb-3">Actions Immediates (7 Jours)</h3>
                  <ul className="space-y-2 text-sm text-white/80">
                    <li>• Reparer parcours reservation (P0)</li>
                    <li>• Schema.org + Llms.txt</li>
                    <li>• Optimisation GMB 100%</li>
                    <li>• Lancement campagne avis</li>
                  </ul>
                </div>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
                  <h3 className="text-stratos-gold font-bold mb-3">Resultats 3 Mois</h3>
                  <ul className="space-y-2 text-sm text-white/80">
                    <li>• Score 59 → 75-80/100</li>
                    <li>• 15+ mots-cles page 1</li>
                    <li>• Top 3 Local Pack</li>
                    <li>• +40-60 resa/mois</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="grid grid-cols-2 gap-8 mb-8">
              <Card className="bg-white p-10 shadow-xl">
                <h3 className="text-3xl font-bold text-gray-900 mb-8 pb-4 border-b-2 border-stratos-gold">
                  Vos Contacts STRATOS
                </h3>
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Email Principal</p>
                    <p className="text-2xl font-bold text-gray-900">admin@stratos-hospitality.com</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Telephone Direct</p>
                    <p className="text-2xl font-bold text-stratos-gold">+33 6 64 27 60 32</p>
                  </div>
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600">Disponible 9h-19h du lundi au vendredi</p>
                  </div>
                </div>
              </Card>

              <Card className="bg-gradient-to-br from-green-600 to-green-500 p-10 shadow-xl text-white">
                <div className="text-center">
                  <p className="text-lg mb-4 opacity-90">ROI Attendu Pack Premium</p>
                  <div className="text-8xl font-bold mb-6">300-400%</div>
                  <p className="text-xl mb-4">sur 12 mois</p>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 mt-6">
                    <p className="text-sm">Investissement: 24,000 EUR</p>
                    <p className="text-sm">Retour attendu: 96,000 EUR</p>
                    <p className="text-lg font-bold mt-2">Net: +72,000 EUR</p>
                  </div>
                </div>
              </Card>
            </div>

            <div className="text-center py-6 border-t border-white/20">
              <p className="text-white/60 text-sm">
                Document confidentiel - STRATOS Hospitality - 12 Janvier 2026 - Page 12/12
              </p>
            </div>
          </div>
        </div>

        {/* Styles Print */}
        <style>{`
          @media print {
            .page-break {
              page-break-after: always;
            }
            @page {
              margin: 15mm;
              size: A4 portrait;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          
          @media screen {
            .max-w-\\[240mm\\] {
              max-width: 260mm;
            }
          }
        `}</style>

      </div>
    </div>
  );
};
